import {
  search,
  searchCategory,
  searchCuratedCategory
} from "../../../../../../lib/api/search";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { requestProducts, receiveProducts } from "../../store/actionCreators";
import logger from "../../../../../../lib/logger";
import { getPageSize, getAdditionalQueryParams } from "../../../selectors";
import { getLocation } from "../../../../router/selectors";
import {
  getSearchApiDefaults,
  getNorthAmericanRegion
} from "../../../../config/selectors";
import config from "../../../../../../config/common";
import { clearError, setError } from "../../../../error";
import { ERROR_ORIGIN } from "../../../../../../components/ErrorAlert/constants";
import { CATEGORY_PAGE } from "../../../../page/constants";
import clientSideRedirect from "../../../../../../client/clientSideRedirect";
import getPageNumberFromQuery from "../../../domains/query/helpers/getPageNumberFromQuery";
import { queryStringToObject } from "../../../../../../helpers/queryStringToObject";
import { getAdvertisingQueryParams } from "../../../advertising/selectors/getAdvertisingQueryParams";
import { getCategoryTreatments } from "../../../../category/selectors";
import { getPageName } from "../../../../page/selectors";
import { clearCuratedCategoryTreatments } from "../../../../category";

const pageSize = config.api.product.search.pageSize;

// eslint-disable-next-line complexity
export const getSearchActionCreator = ({
  state,
  refine,
  sort,
  dispatch,
  useCuratedSearchFallback
}) => {
  const pageName = getPageName(state);
  const isCategory = pageName === CATEGORY_PAGE;

  if (!isCategory) {
    return search;
  }

  const hasTreatments = getCategoryTreatments(state);

  const curatedSearchWrapper = useCuratedSearchFallback
    ? (...args) =>
        searchCuratedCategory.apply(this, args).catch(error => {
          error.message = `Curated Search failed for cid ${args[0]?.cid}, falling back to regular search`;

          logger.error(error);

          dispatch(clearCuratedCategoryTreatments());

          return searchCategory.apply(this, args);
        })
    : searchCuratedCategory;

  return hasTreatments && !refine && !sort
    ? curatedSearchWrapper
    : searchCategory;
};

export const getSearchAction = (state, payload = {}) => {
  const newPayload = {
    ...payload
  };

  const northAmericanRegion = getNorthAmericanRegion(state);

  if (northAmericanRegion) {
    newPayload.region = northAmericanRegion;
  }

  return getSearchActionCreator({
    state,
    refine: payload?.refine,
    sort: payload?.sort
  })(newPayload);
};

export const getLocationQuery = location => {
  const query = location?.query;
  const isObjectQuery =
    Object.prototype.toString.call(query) === "[object Object]";
  const isEmptyObjectQuery = isObjectQuery && Object.keys(query).length === 0;

  if (query != null && (!isEmptyObjectQuery || !location?.search)) {
    return query;
  }

  return queryStringToObject(location?.search || "");
};

export const getQueryObject = (
  { state, isNewPage },
  paginationOffset = null
) => {
  const query = getLocationQuery(getLocation(state));
  const limit = getPageSize(state);

  const queryObject = {
    ...query,
    ...getSearchApiDefaults(state, isNewPage),
    ...getAdvertisingQueryParams(state),
    offset:
      paginationOffset !== null
        ? paginationOffset
        : pageSize * (getPageNumberFromQuery(query) - 1),
    limit
  };

  const northAmericanRegion = getNorthAmericanRegion(state);

  if (northAmericanRegion) {
    queryObject.region = northAmericanRegion;
  }

  return queryObject;
};

const runFetchProduct = ({
  isCategory,
  uuid,
  xSiteOriginFromServerRequest,
  isNewPage,
  dispatch,
  getState
}) => {
  const state = getState();
  const query = getQueryObject({ state, isNewPage });

  dispatch(requestProducts({ query, isNewPage }));
  const searchFunction = getSearchActionCreator({
    state,
    refine: query.refine,
    sort: query.sort,
    dispatch,
    useCuratedSearchFallback: true
  });

  const commonThings = async response => {
    if (isNewPage && (await clientSideRedirect({ response, state }))) {
      return null;
    }

    dispatch(clearError());

    await dispatch(
      receiveProducts({
        ...response,
        query,
        isNewPage
      })
    );
  };

  return searchFunction(query, uuid, xSiteOriginFromServerRequest)
    .then(response => {
      const { products } = response;
      const additionalQueryParams = getAdditionalQueryParams(state);
      const hasFloorFilter =
        query?.refine?.includes("floor") ||
        additionalQueryParams?.refine?.includes("floor");

      if (!isCategory && products?.length === 0 && hasFloorFilter) {
        return searchFunction(
          query,
          uuid,
          xSiteOriginFromServerRequest,
          true
        ).then(response => commonThings(response));
      }

      return commonThings(response);
    })
    .catch(error => {
      if (isNewPage) {
        dispatch(receiveProducts(error));
        throw error;
      }
      error.message = `${
        isCategory
          ? `${error.message} fetchCategory failed`
          : `${error.message} fetchSearch for ${query.q} failed`
      }`;
      logger.error(error);
      dispatch(
        setError({
          message: error.message,
          actionType: receiveProducts.type,
          errorOrigin: ERROR_ORIGIN.search
        })
      );

      return dispatch(receiveProducts(error));
    });
};

const createFetchThunk = isCategory => (
  uuid,
  xSiteOriginFromServerRequest,
  isNewPage
) => (dispatch, getState) =>
  runFetchProduct({
    isCategory,
    uuid,
    xSiteOriginFromServerRequest,
    isNewPage,
    dispatch,
    getState
  });

export const fetchSearchThunk = createAsyncThunk(
  "search/fetchSearch",
  async (
    { uuid, xSiteOriginFromServerRequest, isNewPage } = {},
    { dispatch, getState }
  ) =>
    runFetchProduct({
      isCategory: false,
      uuid,
      xSiteOriginFromServerRequest,
      isNewPage,
      dispatch,
      getState
    })
);

export const fetchCategoryThunk = createAsyncThunk(
  "search/fetchCategory",
  async (
    { uuid, xSiteOriginFromServerRequest, isNewPage } = {},
    { dispatch, getState }
  ) =>
    runFetchProduct({
      isCategory: true,
      uuid,
      xSiteOriginFromServerRequest,
      isNewPage,
      dispatch,
      getState
    })
);

export const fetchSearch = createFetchThunk(false);
export const fetchCategory = createFetchThunk(true);
